// src/modules/app-views/invoices/pages/Document/tabs/PreviewTab/index.jsx
// *******************************************************************************
// INVOICE PREVIEW TAB - DANFE NFC-e
// *******************************************************************************
// Tab for previewing and printing the fiscal coupon (DANFE NFC-e).
// Displays all invoice data in a print-ready format with QR Code.
// Actions: Print, Download PDF, Send Email, Send WhatsApp.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useState } from 'react';
import { Button, Space, Divider, Typography, Alert } from 'antd';
import {
  PrinterOutlined,
  FilePdfOutlined,
  MailOutlined,
  WhatsAppOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import invoicesService from 'modules/app-views/invoices/service';
import IssuerSection from './IssuerSection';
import ItemsSection from './ItemsSection';
import TotalsSection from './TotalsSection';
import ConsumerSection from './ConsumerSection';
import FiscalInfoSection from './FiscalInfoSection';
import './PreviewTab.less';

const { Title, Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PreviewTab = ({ onOpenEmailModal, onOpenWhatsappModal, onPrint }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isNew = !data?._id || data?._id === 'add';
  const status = data?.status;
  const isAuthorized = status === 'authorized';

  // Determine watermark text based on status
  const watermarkText = getWatermarkText(status);
  const showWatermark = !isAuthorized;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState({
    print: false,
    pdf: false,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // PRINT - Uses passed handler or fallback to service
  const handlePrint = useCallback(async () => {
    setLoading(prev => ({ ...prev, print: true }));

    try {
      if (typeof onPrint === 'function') {
        await onPrint();
      } else {
        // Pass invoice data for direct USB printing (ESC/POS)
        await invoicesService.printDanfe('danfe-print-container', {
          invoiceData: data,
        });
      }
    } catch (error) {
      console.error('[PREVIEW] Print error:', error);
    } finally {
      setLoading(prev => ({ ...prev, print: false }));
    }
  }, [onPrint, data]);

  // DOWNLOAD PDF - Uses invoicesService.downloadDanfe
  const handleDownloadPdf = useCallback(async () => {
    const invoiceId = data?._id;
    if (!invoiceId) return;

    setLoading(prev => ({ ...prev, pdf: true }));

    try {
      const documentNumber = data?.documentNumber || '';
      const filename = `danfe-${documentNumber || invoiceId}.pdf`;

      await invoicesService.downloadDanfe(invoiceId, filename);
    } catch (error) {
      console.error('[PREVIEW] Download PDF error:', error);
      appService.message({
        flag: 'error',
        text: translateX('download_pdf_error'),
      });
    } finally {
      setLoading(prev => ({ ...prev, pdf: false }));
    }
  }, [data?._id, data?.documentNumber]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Show message for new invoices
  if (isNew) {
    return (
      <div className="container" style={{ padding: 24 }}>
        <Alert
          type="info"
          showIcon
          title={translateX('preview_not_available')}
          description={translateX('preview_save_first')}
        />
      </div>
    );
  }

  return (
    <div className="container">
      {/* Action Buttons - Hidden when printing */}
      {/*
      <div className="preview-actions no-print">
        <Space wrap>
          <Button
            type="primary"
            icon={loading.print ? <LoadingOutlined /> : <PrinterOutlined />}
            onClick={handlePrint}
            loading={loading.print}
          >
            {translateX('print')}
          </Button>

          <Button
            icon={loading.pdf ? <LoadingOutlined /> : <FilePdfOutlined />}
            onClick={handleDownloadPdf}
            disabled={!isAuthorized}
            loading={loading.pdf}
          >
            {translateX('download_pdf')}
          </Button>

          <Button icon={<MailOutlined />} onClick={onOpenEmailModal} disabled={!isAuthorized}>
            {translateX('send_email')}
          </Button>

          <Button
            icon={<WhatsAppOutlined />}
            onClick={onOpenWhatsappModal}
            disabled={!isAuthorized}
            style={isAuthorized ? { borderColor: '#25D366', color: '#25D366' } : {}}
          >
            {translateX('send_whatsapp')}
          </Button>
        </Space>
      </div>
*/}

      {/* Status Alert */}
      {!isAuthorized && (
        <div className="no-print" style={{ marginBottom: 16 }}>
          <Alert
            type={getAlertType(status)}
            showIcon
            title={translateX(`invoice_status_${status || 'draft'}`)}
            description={getStatusDescription(status)}
          />
        </div>
      )}

      {/* DANFE Preview Container - danfe-print-mode forces black text for print */}
      <div id="danfe-print-container" className="danfe-container danfe-print-mode">
        {/* Watermark for non-authorized invoices */}
        {showWatermark && <div className="danfe-watermark">{watermarkText}</div>}

        {/* DANFE Content */}
        <div className="danfe-content">
          {/* Header - Document Type */}
          <div className="danfe-header">
            <Title level={5} style={{ margin: 0, textAlign: 'center' }}>
              DANFE NFC-e
            </Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 11 }}>
              Documento Auxiliar da Nota Fiscal de Consumidor Eletrônica
            </Text>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Section 1: Issuer Data */}
          <IssuerSection data={data} />

          <Divider dashed style={{ margin: '12px 0' }} />

          {/* Section 2: Items List */}
          <ItemsSection data={data} />

          <Divider dashed style={{ margin: '12px 0' }} />

          {/* Section 3: Totals and Payments */}
          <TotalsSection data={data} />

          <Divider dashed style={{ margin: '12px 0' }} />

          {/* Section 4: Consumer Data */}
          <ConsumerSection data={data} />

          <Divider dashed style={{ margin: '12px 0' }} />

          {/* Section 5: Fiscal Info, Access Key, QR Code */}
          <FiscalInfoSection data={data} />
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PreviewTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get watermark text based on invoice status
 * @param {string} status - Invoice status
 * @returns {string} Watermark text
 */
function getWatermarkText(status) {
  const texts = {
    draft: 'RASCUNHO',
    pending: 'AGUARDANDO',
    rejected: 'REJEITADA',
    denied: 'DENEGADA',
    canceled: 'CANCELADA',
  };
  return texts[status] || 'RASCUNHO';
}

/**
 * Get alert type based on invoice status
 * @param {string} status - Invoice status
 * @returns {string} Alert type
 */
function getAlertType(status) {
  const types = {
    draft: 'info',
    pending: 'warning',
    rejected: 'error',
    denied: 'error',
    canceled: 'warning',
  };
  return types[status] || 'info';
}

/**
 * Get status description for alert
 * @param {string} status - Invoice status
 * @returns {string} Status description
 */
function getStatusDescription(status) {
  const descriptions = {
    draft: 'Este documento ainda não foi transmitido para a SEFAZ.',
    pending: 'Aguardando resposta da SEFAZ.',
    rejected: 'Documento rejeitado pela SEFAZ. Verifique os erros e corrija.',
    denied: 'Documento denegado pela SEFAZ.',
    canceled: 'Este documento foi cancelado.',
  };
  return descriptions[status] || '';
}
